import { EmbeddableFlowSubplaceWithoutRouter } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType,ButtonWidget } from '@tableau/widgets';
import * as queryString from 'query-string';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { ContentSpecificControls } from '~src/app/spaces/Explorer/ExplorerToolbar';
import { RunFlowTaskDefinition } from '~src/contentDefinitions/RunFlowTaskDefinition';
import { SubscriptionDefinition } from '~src/contentDefinitions/SubscriptionDefinition';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../../app/spaces/SpaceTelemetry';
import {explorerSubspace } from '../../app/spaces/SubspaceHelpers';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { FlowSubspaceName } from '../../app/state/SpaceInfo';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { Messages } from '../../codegen/Localize';
import { ISanitizer } from '../../commonInfra/utils/Sanitize';
import { FlowOutputStepRunDefinition } from '../../contentDefinitions/FlowOutputStepRunDefinition';
import { FlowDetailWithUsageInfo, FlowListItemInfo } from '../../ts/models/FlowInfo';
import { IPlace } from '../../ts/models/IPlace';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { DataCatalogUIActionsService } from '../../ts/services/DataCatalogUIActionsService';
import { PublishFlowAction } from '../actions/PublishFlowAction';
import { Flows } from '../resources/Flows';
import { FlowSubplaceProps } from '../types/FlowSubplaceProps';
import { openCreateFlowRunTaskDialog } from '../utils/FlowDialogUtils';
import { FlowConnectionsSubplace } from './FlowConnectionsSubplace';
import { flowHasIncompatibleDocumentVersion, FlowOverviewSubplace } from './FlowOverviewSubplace';
import { FlowRunSchedulesSubplace } from './FlowRunSchedulesSubplace';
import { FlowSpaceHeader } from './FlowSpaceHeader';
import { showCreateFlowSubscriptionDialog } from './FlowSubscriptionCreationDialog';

export type FlowSpaceStateProps = {
  readonly catalogingOnSiteEnabled: boolean;
  readonly schedulingFlowsEnabled: boolean;
  readonly urls: SpacesSiteUrls;
  readonly areUsersVisible: boolean;
  readonly flowOutputSubscriptionsEnabledOnSite: boolean;
};

export type FlowSpaceDispatchProps = {
  readonly enterSpace: (flowDetailInfo?: FlowDetailWithUsageInfo) => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
};

export interface FlowSpaceOwnProps {
  readonly routeLocationSearch: string;
  readonly showPublishResult: boolean;

  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowDetailInfo: FlowDetailWithUsageInfo;
  readonly flowListItemInfo: FlowListItemInfo;
  readonly projectHierarchy: INamedItemInfo[];

  readonly refreshData: () => void;

  // subplace info
  readonly subplaceName: string;

  // Angular services
  readonly place: IPlace;
  readonly sanitizer: ISanitizer;
  readonly siteUrls: SiteUrls;
}

export type FlowSpaceProps = FlowSpaceStateProps & FlowSpaceDispatchProps & FlowSpaceOwnProps;

function overviewSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return {
    name: 'overview',
    tabLabel: Messages.overview(),
    renderContent: () => <FlowOverviewSubplace {...subplaceProps} />,
    shouldRenderTabContentCount: false
  };
}

function connectionsSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return {
    name: 'connections',
    tabLabel: Messages.connections(),
    renderContent: () => <FlowConnectionsSubplace {...subplaceProps} />,
    shouldRenderTabContentCount: false
  };
}

function scheduledTasksSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  const toolbarContentSpecificControlsProps: ContentSpecificControls =
    { beginning: subplaceProps.flowAllowedActions.runFlow ? renderCreateTaskButton(subplaceProps) : null};
  if (FeatureFlags.LinkedTaskSchedulingEnabled) {
    return explorerSubspace({
      name: 'scheduledTasks',
      tabLabel: Messages.scheduledTasks(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      toolbarContentSpecificControls: toolbarContentSpecificControlsProps,
      supportFilterPanel: false,
      hideSelection: false,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.flowTasksEmptyMessageWithPermissions() // TODO w/out perms
      }
    });
  } else {
    return {
      name: 'scheduledTasks',
      tabLabel: Messages.scheduledTasks(),
      renderContent: () => <FlowRunSchedulesSubplace {...subplaceProps} />,
      shouldRenderTabContentCount: false
    };
  }
}

function lineageSubspace(subplaceProps: FlowSubplaceProps, areUsersVisible: boolean): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    renderContent: () =>
      <EmbeddableFlowSubplaceWithoutRouter
        flowVizportalUrlId={subplaceProps.flowDetailInfo.id}
        client={catalogApolloClient}
        siteUrls={subplaceProps.siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
      />,
    shouldRenderTabContentCount: false,
  };
}

function runHistorySubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return explorerSubspace({
    name: 'runHistory',
    tabLabel: Messages.runHistory(),
    supportedDisplayModes: FlowOutputStepRunDefinition.supportedDisplayModes,
    shouldRenderContentCount: false,
    supportFilterPanel: true,
    hideSelection: true,
    toolbarVisibility: 'enabled',
    emptyStateProps: {
      primaryMessage: Messages.flowsEmptyRunHistory()
    }
  });
}

function renderCreateSubscriptionButton(id: string, name: string): JSX.Element {
  return (
    <ButtonWidget
      testId={'subscribe-to-flow-button-test-id'}
      buttonType={ ButtonType.Outline }
      handleClick={() => { showCreateFlowSubscriptionDialog(id, name);} }
    >
      {Messages.flowSubscriptionsSubscribe()}
    </ButtonWidget>
  );
}

function subscriptionsSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return explorerSubspace({
    name: 'subscriptions',
    tabLabel: Messages.subscriptions(),
    supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
    shouldRenderContentCount: false,
    toolbarVisibility: 'enabled',
    emptyStateProps: {
      primaryMessage: Messages.flowSubscriptionsEmptyMessage()
    },
    toolbarContentSpecificControls: {
      beginning: subplaceProps.flowAllowedActions.createSubscription ?
        renderCreateSubscriptionButton(subplaceProps.flowDetailInfo.id, subplaceProps.flowDetailInfo.name)
        : null
    },
  });
}

function getFlowSubspaces(
  subplaceProps: FlowSubplaceProps,
  catalogingOnSiteEnabled: boolean,
  schedulingFlowsEnabled: boolean,
  areUsersVisible: boolean,
  flowOutputSubscriptionsEnabledOnSite: boolean
): ReadonlyArray<Subspace> {
  if (flowHasIncompatibleDocumentVersion(subplaceProps)) {
    // (mechen) for flow with incompatible doc version, only show overview tab, since
    // there wont be any step/connections information and user cannot schedule/run the flow
    return [ overviewSubspace(subplaceProps) ];
  }
  return _.compact([
    overviewSubspace(subplaceProps),
    connectionsSubspace(subplaceProps),
    schedulingFlowsEnabled && scheduledTasksSubspace(subplaceProps),
    schedulingFlowsEnabled && runHistorySubspace(subplaceProps),
    schedulingFlowsEnabled && flowOutputSubscriptionsEnabledOnSite && subscriptionsSubspace(subplaceProps),
    catalogingOnSiteEnabled && lineageSubspace(subplaceProps, areUsersVisible),
  ]);
}

function renderCreateTaskButton(subplaceProps: FlowSubplaceProps): JSX.Element {
  return (
    <ButtonWidget
      handleClick={() => openCreateFlowRunTaskDialog(subplaceProps.flowDetailInfo)}
      buttonType={ButtonType.Outline}
      testId={'ScheduledTaskCreateRunFlowTaskTestId'}
    >
      {Messages.newTask()}
    </ButtonWidget>
  );
}

export class FlowSpace extends React.PureComponent<FlowSpaceProps> {

  private memoizedGetFlowSubspaces = defaultMemoize(getFlowSubspaces);

  constructor(props: FlowSpaceProps) {
    super(props);
    props.enterSpace();
  }

  public exploreFlowUrl = (subspaceName?: FlowSubspaceName)  => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.flowOverviewUrl(this.props.flowDetailInfo.id);
      case 'connections':
        return this.props.urls.flowConnectionsUrl(this.props.flowDetailInfo.id);
      case 'scheduledTasks':
        return this.props.urls.flowScheduledTasksUrl(this.props.flowDetailInfo.id);
      case 'runHistory':
        return this.props.urls.flowRunHistoryUrl(this.props.flowDetailInfo.id);
      case 'subscriptions':
        return this.props.urls.flowSubscriptionsUrl(this.props.flowDetailInfo.id);
      case 'lineage':
        return this.props.urls.flowLineageUrl(this.props.flowDetailInfo.id);
    }
  }

  public render() {
    const subplaceProps: FlowSubplaceProps = {
      enterSpace: this.props.enterSpace,
      flowAllowedActions: this.props.flowAllowedActions,
      flowDetailInfo: this.props.flowDetailInfo,
      refreshData: this.props.refreshData,
      sanitizer: this.props.sanitizer,
      siteUrls: this.props.urls,
    };

    const subspaces = this.memoizedGetFlowSubspaces(
      subplaceProps,
      this.props.catalogingOnSiteEnabled,
      this.props.schedulingFlowsEnabled,
      this.props.areUsersVisible,
      this.props.flowOutputSubscriptionsEnabledOnSite
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as FlowSubspaceName}
        urlForSubspace={this.exploreFlowUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'flow'}
        spaceItemName={this.props.flowDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.flowDetailInfo);
    this.updateBreadcrumbs();
    Flows.markRecentlyViewed(this.props.flowDetailInfo.id);

    if (this.props.showPublishResult) {
      const queryParams = queryString.parse(this.props.routeLocationSearch);
      const publishedDataSourcesParam = queryParams[UrlParamKeys.PublishedDataSources];
      const publishedDataSourceUrlNames = publishedDataSourcesParam ? publishedDataSourcesParam.split(',') : [];
      PublishFlowAction.handlePublishedFlow(this.props.flowDetailInfo, publishedDataSourceUrlNames);
    }
  }

  public componentDidUpdate(prevProps: FlowSpaceProps) {
    if (this.props.flowDetailInfo !== prevProps.flowDetailInfo ||
        this.props.subplaceName !== prevProps.subplaceName) {
      this.props.enterSpace();
      this.updateBreadcrumbs();
    }
    if (this.props.flowDetailInfo.id !== prevProps.flowDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.flowDetailInfo);
      Flows.markRecentlyViewed(this.props.flowDetailInfo.id);
    }
  }

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    populateContentBreadcrumbs([ ...projectHierarchyWithType, this.props.flowListItemInfo ], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const flowDetailsWithUsage = {
      usageInfo: this.props.flowListItemInfo.usageInfo,
      usageInfoMaxValues: this.props.flowListItemInfo.usageInfoMaxValues,
      ...this.props.flowDetailInfo
    };

    return <FlowSpaceHeader
      item={flowDetailsWithUsage}
      availableActions={this.props.flowAllowedActions}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      urls={this.props.urls}
      catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
    />;
  }
}